import React, { useState, useEffect } from "react";
import { Card, CardContent, Typography, Box } from "@mui/material";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TableContainer from "@mui/material/TableContainer";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import "dayjs/locale/en-gb";
import DateRangePicker from "@wojtekmaj/react-daterange-picker";
import "@wojtekmaj/react-daterange-picker/dist/DateRangePicker.css";
import "react-calendar/dist/Calendar.css";

dayjs.extend(utc);
dayjs.extend(timezone);
// Set locale to British English for DD/MM/YYYY
dayjs.locale("en-gb");

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const BACKEND_TOKEN = process.env.REACT_APP_BACKEND_TOKEN;

export default function Distance({ deviceId, fallbackDays = 7, chassisNum }) {
	const now = dayjs();
	const [startDate, setStartDate] = useState(
		now.subtract(fallbackDays, "day").startOf("day")
	);
	const [endDate, setEndDate] = useState(now.endOf("day"));
	const [dateRange, setDateRange] = useState([
		startDate.toDate(),
		endDate.toDate(),
	]);
	const [data, setData] = useState([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(null);

	const handleDateRangeChange = (range) => {
		if (!range) return;
		const [start, end] = range;
		if (start) setStartDate(dayjs(start));
		if (end) setEndDate(dayjs(end));
		setDateRange(range);
	};

	const formatWithOffset = (dayjsDate, isEnd) => {
		const d = isEnd ? dayjsDate.endOf("day") : dayjsDate.startOf("day");
		const jsDate = d.toDate();
		const pad = (n) => String(n).padStart(2, "0");
		const tzOffsetMinutes = -jsDate.getTimezoneOffset();
		const sign = tzOffsetMinutes >= 0 ? "+" : "-";
		const absOffset = Math.abs(tzOffsetMinutes);
		const hours = pad(Math.floor(absOffset / 60));
		const minutes = pad(absOffset % 60);
		const localIso = d.format("YYYY-MM-DDTHH:mm:ss");
		return `${localIso}${sign}${hours}:${minutes}`;
	};

	const fetchData = React.useCallback(async () => {
		setLoading(true);
		setError(null);
		try {
			const startParam = formatWithOffset(startDate, false);
			const endParam = formatWithOffset(endDate, true);
			const url = `${BACKEND_URL}devices/${deviceId}/daily-distance/?start_iso=${encodeURIComponent(
				startParam
			)}&end_iso=${encodeURIComponent(endParam)}`;
			const resp = await fetch(url, {
				headers: {
					Accept: "application/json",
					Authorization: `Bearer ${BACKEND_TOKEN}`,
				},
			});
			if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
			const json = await resp.json();
			setData(json.data || []);
		} catch (e) {
			setError(e.message);
		} finally {
			setLoading(false);
		}
	}, [startDate, endDate, deviceId]);

	useEffect(() => {
		fetchData();
	}, [fetchData]);

	return (
		<Card sx={{ p: 2, mb: 2, overflow: "visible" }}>
			<Typography variant="subtitle1">
				{`Daily Distance - `}
				<Typography variant="caption">{chassisNum}</Typography>
			</Typography>
			<CardContent
				sx={{ overflow: "visible", minHeight: { xs: 420, sm: 480 } }}
			>
				<LocalizationProvider
					dateAdapter={AdapterDayjs}
					adapterLocale="en-gb"
				>
					<Box
						sx={{
							mb: 2,
							"& .react-calendar": {
								zIndex: 1500,
								position: "relative",
							},
						}}
					>
						<DateRangePicker
							onChange={handleDateRangeChange}
							value={dateRange}
							clearIcon={null}
							maxDate={new Date()}
						/>
					</Box>
				</LocalizationProvider>

				{loading ? (
					<Typography>Loading daily distances...</Typography>
				) : error ? (
					<Typography color="error">Error: {error}</Typography>
				) : data.length > 0 ? (
					<TableContainer>
						<Table>
							<TableHead>
								<TableRow>
									<TableCell>Date</TableCell>
									<TableCell align="right">
										Distance (km)
									</TableCell>
								</TableRow>
							</TableHead>
							<TableBody>
								{data.map((row, idx) => (
									<TableRow key={idx} hover>
										<TableCell>{row.date}</TableCell>
										<TableCell align="right">
											{row.distance_km}
										</TableCell>
									</TableRow>
								))}
							</TableBody>
						</Table>
					</TableContainer>
				) : (
					// Reserve height when empty to prevent container collapse
					<Box
						sx={{
							height: 320,
							border: "1px dashed",
							borderColor: "divider",
							borderRadius: 1,
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
							color: "text.secondary",
						}}
					>
						No data available for this range.
					</Box>
				)}
			</CardContent>
		</Card>
	);
}
